/**
 * Auto Fleet Generator Modal
 * 
 * Main modal component with stepper for fleet generation workflow.
 * Orchestrates the 3-step process: Mode Selection → Configuration → Preview
 */

import React, { useState, useContext, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  IconButton,
  Alert,
} from '@mui/material';

import { AppContext, Layer, Point } from '../../../../context/context';
import { useFixedRoutes } from '../../../../context/FixedRoutesContext';
import ModeSelectionStep from './ModeSelectionStep';
import FixedRouteModeSelectionStep from './FixedRouteModeSelectionStep';
import FixedRouteConfigurationStep from './FixedRouteConfigurationStep';
import MixedServiceModeSelectionStep from './MixedServiceModeSelectionStep';
import MixedServiceConfigurationStep from './MixedServiceConfigurationStep';
import ConfigurationStep from './ConfigurationStep';
import PreviewStep from './PreviewStep';
import { GeneratorConfig, GeneratedFleetData, GeneratorMode, TripAnalysisResult, GeneratedShift } from './types';
import { ShiftGenerator } from './shiftGenerator';
import { parseTime, calculateTimeDifference } from './timeUtils';
import { Vehicle } from '../../../../types/vehicle';
import { BreakPeriod } from '../../../../types/simulation';
import { logEvent } from '../../../../services/analytics';
import { FleetOptimizer } from '../../../../utils/fixedRoutes/fleetOptimizer';
import { convertAssignmentsToFleet } from '../../../../utils/fixedRoutes/fleetConverter';
import type { FixedRouteFleetConfig, MixedFleetConfig, MixedFleetSummary } from '../../../../utils/fixedRoutes/fleetTypes';
import { validateRoutes } from '../../../../utils/fixedRoutes/routeValidator';
import { getTripLayersFromContext } from './tripAnalysis';
import type { Line } from '../../../../context/context';
import {
  buildOperatingPeriodsFromStatuses,
  convertVehiclesToGeneratedShifts,
  getNextTimelineStatus,
  getOperatingPeriodStatusAtBlock,
  recalculateFleetSummary,
  toTimelineSlotKey,
  type TimelineSlotOverrides,
} from './timelineEditing';
import { generateFleetFromTripLayerAnalysis } from './tripLayerFleetGeneration';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
interface AutoFleetGeneratorModalProps {
  open: boolean;
  onClose: () => void;
  onFleetCreated: (fleetName: string, vehicles: Vehicle[]) => void;
}

const STEP_KEYS = ['selectMode', 'configureFleet', 'previewAndCreate'] as const;

// Stamp each break with the vehicle's start coordinates so the downstream
// orchestrator never falls back to (0, 0) — Null Island makes a shift
// infeasible and silently zeroes the solver output.
const attachBreakLocations = <T extends { breaks?: BreakPeriod[] | undefined }>(
  operatingPeriods: T[] | undefined,
  startCoords: { lat: number; lng: number } | null | undefined
): T[] => {
  if (!operatingPeriods) return operatingPeriods as unknown as T[];
  if (!startCoords || !Number.isFinite(startCoords.lat) || !Number.isFinite(startCoords.lng)) {
    return operatingPeriods;
  }
  const locationString = `${startCoords.lat},${startCoords.lng}`;
  return operatingPeriods.map(period => ({
    ...period,
    breaks: period.breaks?.map(brk =>
      brk.location && brk.location.trim() !== '' ? brk : { ...brk, location: locationString }
    ),
  }));
};

const ensureValidServiceWindow = (
  suggestedStart: string,
  suggestedEnd: string
): { start: string; end: string } => {
  const startMinutes = parseTime(suggestedStart);
  const endMinutes = parseTime(suggestedEnd);
  const duration = calculateTimeDifference(startMinutes, endMinutes);

  if (duration > 0) {
    return { start: suggestedStart, end: suggestedEnd };
  }

  const fallbackEndMinutes = (startMinutes + 60) % (24 * 60);
  const fallbackEnd = `${Math.floor(fallbackEndMinutes / 60).toString().padStart(2, '0')}:${(fallbackEndMinutes % 60).toString().padStart(2, '0')}`;
  return {
    start: suggestedStart,
    end: fallbackEnd,
  };
};

// Default configuration
const getDefaultConfig = (): GeneratorConfig => ({
  enableShiftAllocation: false, // Default to Phase 1A behavior (no shift allocation)
  mode: 'manual',
  selectedLayerId: undefined,
  manualVehicleCount: 5,
  selectedRouteIds: [],
  vehicleSharingMode: 'shared',
  serviceStartTime: '06:00',
  serviceEndTime: '22:00',
  maxShiftTimeMinutes: 480, // 8 hours
  breakLengthMinutes: 30,
  minTimeBeforeBreakMinutes: 120, // 2 hours
  minTimeAfterBreakMinutes: 120, // 2 hours
  standardSeats: 10,
  wheelchairSeats: 1,
  startLocationId: '',
  endLocationId: '',
  peakPeriods: [],
  breakReductionDuringPeakMinutes: 0,
  staggerBreaks: true,
  tripsPerVehiclePerHour: 5,
  demandCurve: undefined,
  hourlyVehicleRequirements: undefined,
  useDemandBasedAllocation: false,
  flexibilityPercent: 0,
});

const AutoFleetGeneratorModal: React.FC<AutoFleetGeneratorModalProps> = ({
  open,
  onClose,
  onFleetCreated,
}) => {
  const { t } = useTranslation('mapWizards');
  const context = useContext(AppContext);
  const mapLayers = context?.global?.mapLayers || [];
  const { routes: allFixedRoutes, stops: allFixedRouteStops, trafficMultiplier: globalTrafficMultiplier } = useFixedRoutes();
  
  // Extract points from layers (full Point objects)
  const availablePoints: Point[] = mapLayers.flatMap(layer =>
    layer.shapes.filter((s): s is Point => s.type === 'point')
  );

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);

  // Configuration state
  const [config, setConfig] = useState<GeneratorConfig>(getDefaultConfig());
  const [tripAnalysisResult, setTripAnalysisResult] = useState<TripAnalysisResult | undefined>();

  // Generated fleet state
  const [generatedFleetData, setGeneratedFleetData] = useState<GeneratedFleetData | null>(null);
  const [timelineSlotOverrides, setTimelineSlotOverrides] = useState<TimelineSlotOverrides>({});
  const [previewSource, setPreviewSource] = useState<'generated' | 'edited'>('generated');
  const [fleetName, setFleetName] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Helper function to calculate centroid from trip lines
  const calculateTripLayerCentroid = (layerId: string | undefined): { lat: number; lng: number } | null => {
    if (!layerId) return null;
    
    const selectedLayer = mapLayers.find(l => l.layerId.toString() === layerId);
    if (!selectedLayer) return null;
    
    // Get all trip lines from the layer
    const tripLines = selectedLayer.shapes.filter(
      (shape): shape is Line => shape.type === 'line' && 'ride_id' in shape
    ) as Line[];
    
    if (tripLines.length === 0) return null;
    
    // Collect all coordinates from trip lines
    const allCoords: [number, number][] = [];
    tripLines.forEach(line => {
      if (line.coordinates && line.coordinates.length >= 2) {
        // Add origin and destination coordinates
        allCoords.push(line.coordinates[0]); // Origin
        if (line.coordinates.length > 1) {
          allCoords.push(line.coordinates[line.coordinates.length - 1]); // Destination
        }
      }
      
      // Also check if line has origin/dest point IDs
      if ('originPointId' in line && 'destPointId' in line) {
        const originPoint = selectedLayer.shapes.find(
          s => s.id === (line as any).originPointId && s.type === 'point'
        ) as Point | undefined;
        const destPoint = selectedLayer.shapes.find(
          s => s.id === (line as any).destPointId && s.type === 'point'
        ) as Point | undefined;
        
        if (originPoint && originPoint.coordinates) {
          allCoords.push(originPoint.coordinates);
        }
        if (destPoint && destPoint.coordinates) {
          allCoords.push(destPoint.coordinates);
        }
      }
    });
    
    if (allCoords.length === 0) return null;
    
    // Calculate centroid (average of all coordinates)
    const totalLat = allCoords.reduce((sum, coord) => sum + coord[0], 0);
    const totalLng = allCoords.reduce((sum, coord) => sum + coord[1], 0);
    
    return {
      lat: totalLat / allCoords.length,
      lng: totalLng / allCoords.length
    };
  };

  // Helper function to calculate centroid from fixed routes
  const calculateFixedRouteCentroid = (routeIds: string[]): { lat: number; lng: number } | null => {
    if (routeIds.length === 0) return null;
    
    const selectedRoutes = allFixedRoutes.filter(route => routeIds.includes(route.id));
    if (selectedRoutes.length === 0) return null;
    
    // Collect all stop coordinates from selected routes
    // Route.stops contains FixedRouteStopConfig (stopId, waitTimeMinutes)
    // We need to look up the actual FixedRouteStop from allFixedRouteStops to get coordinates
    const allCoords: [number, number][] = [];
    selectedRoutes.forEach(route => {
      route.stops.forEach(stopConfig => {
        const stop = allFixedRouteStops.find(s => s.id === stopConfig.stopId);
        if (stop && stop.coordinates) {
          allCoords.push([stop.coordinates.lat, stop.coordinates.lng]);
        }
      });
    });
    
    if (allCoords.length === 0) return null;
    
    // Calculate centroid
    const totalLat = allCoords.reduce((sum, coord) => sum + coord[0], 0);
    const totalLng = allCoords.reduce((sum, coord) => sum + coord[1], 0);
    
    return {
      lat: totalLat / allCoords.length,
      lng: totalLng / allCoords.length
    };
  };

  // Helper function to calculate centroid from all points
  const calculateAllPointsCentroid = (): { lat: number; lng: number } | null => {
    if (availablePoints.length === 0) return null;
    
    const totalLat = availablePoints.reduce((sum, point) => sum + point.coordinates[0], 0);
    const totalLng = availablePoints.reduce((sum, point) => sum + point.coordinates[1], 0);
    
    return {
      lat: totalLat / availablePoints.length,
      lng: totalLng / availablePoints.length
    };
  };

  // Helper function to get or create a point at coordinates
  const getOrCreatePointAtCoordinates = (
    lat: number,
    lng: number,
    name: string
  ): string => {
    // First, check if a point already exists at this location (within small tolerance)
    const tolerance = 0.0001; // ~11 meters
    const existingPoint = availablePoints.find((p) => {
      const dist = Math.sqrt(
        Math.pow(p.coordinates[0] - lat, 2) + 
        Math.pow(p.coordinates[1] - lng, 2)
      );
      return dist < tolerance;
    });

    if (existingPoint) {
      return existingPoint.id;
    }

    // Create a new point at the location and add it to the map
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const pointId = `fleet-inferred-${timestamp}-${random}`;
    
    const newPoint: Point = {
      id: pointId,
      type: 'point',
      name: name,
      description: `Auto-generated location point for fleet generation`,
      colour: '#FF9800',
      icon: 'location',
      size: 24,
      isLocked: false,
      coordinates: [lat, lng],
      hidden: false,
    };

    // Add the point to the first available layer
    if (context?.global?.setLayers && mapLayers.length > 0) {
      const targetLayer = mapLayers.find(l => l.layerId === context.global.activeLayerId) || mapLayers[0];
      if (targetLayer) {
        newPoint.layerId = targetLayer.layerId;
        const updatedLayer = {
          ...targetLayer,
          shapes: [...targetLayer.shapes, newPoint]
        };
        context.global.setLayers(prev => 
          prev.map(l => l.layerId === targetLayer.layerId ? updatedLayer : l)
        );
      }
    }

    return pointId;
  };

  // Set default start/end locations - infer from trip layer, fixed routes, or use first available point
  React.useEffect(() => {
    if (!config.startLocationId) {
      let inferredCoords: { lat: number; lng: number } | null = null;
      let inferredPointId: string | null = null;

      // Priority 1: Infer from selected trip layer
      if (config.mode === 'trip-layer' && config.selectedLayerId) {
        inferredCoords = calculateTripLayerCentroid(config.selectedLayerId);
        if (inferredCoords) {
          inferredPointId = getOrCreatePointAtCoordinates(
            inferredCoords.lat,
            inferredCoords.lng,
            'Trip Layer Centroid'
          );
        }
      }

      // Priority 2: Infer from fixed routes (if selected)
      if (!inferredCoords && config.mode === 'fixed-routes' && config.selectedRouteIds && config.selectedRouteIds.length > 0) {
        inferredCoords = calculateFixedRouteCentroid(config.selectedRouteIds);
        if (inferredCoords) {
          inferredPointId = getOrCreatePointAtCoordinates(
            inferredCoords.lat,
            inferredCoords.lng,
            'Fixed Routes Centroid'
          );
        }
      }

      // Priority 3: Infer from mixed mode (try trip layer first, then fixed routes)
      if (!inferredCoords && config.mode === 'mixed') {
        if (config.selectedLayerId) {
          inferredCoords = calculateTripLayerCentroid(config.selectedLayerId);
          if (inferredCoords) {
            inferredPointId = getOrCreatePointAtCoordinates(
              inferredCoords.lat,
              inferredCoords.lng,
              'Mixed Service Centroid'
            );
          }
        }
        if (!inferredCoords && config.selectedRouteIds && config.selectedRouteIds.length > 0) {
          inferredCoords = calculateFixedRouteCentroid(config.selectedRouteIds);
          if (inferredCoords) {
            inferredPointId = getOrCreatePointAtCoordinates(
              inferredCoords.lat,
              inferredCoords.lng,
              'Mixed Service Centroid'
            );
          }
        }
      }

      // Priority 4: Use centroid of all points on map
      if (!inferredCoords) {
        inferredCoords = calculateAllPointsCentroid();
        if (inferredCoords) {
          inferredPointId = getOrCreatePointAtCoordinates(
            inferredCoords.lat,
            inferredCoords.lng,
            'Map Centroid'
          );
        }
      }

      // Priority 5: Use first available point
      if (!inferredPointId && availablePoints.length > 0) {
        inferredPointId = availablePoints[0].id;
      }

      if (inferredPointId) {
        setConfig(prev => ({
          ...prev,
          startLocationId: inferredPointId!,
          endLocationId: inferredPointId!,
        }));
      }
    }
  }, [availablePoints, config.startLocationId, config.mode, config.selectedLayerId, config.selectedRouteIds, mapLayers, allFixedRoutes, allFixedRouteStops, context]);

  // Handle configuration changes
  const handleConfigChange = useCallback((updates: Partial<GeneratorConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, []);

  // Handle mode selection analysis results
  const handleAnalysisComplete = useCallback((result: TripAnalysisResult | undefined) => {
    setTripAnalysisResult(result);
    
    // Update config with analysis results
    if (result) {
      const normalizedWindow = ensureValidServiceWindow(
        result.suggestedServiceStart,
        result.suggestedServiceEnd
      );

      // Convert detected peak periods to PeakPeriod format
      const peakPeriods = (result.detectedPeakPeriods || []).map((peak) => ({
        start: peak.start,
        end: peak.end,
      }));

      setConfig(prev => ({
        ...prev,
        manualVehicleCount: result.calculatedVehicles,
        serviceStartTime: normalizedWindow.start,
        serviceEndTime: normalizedWindow.end,
        demandCurve: result.hourlyDemand, // Pass hourly demand for shift optimization
        hourlyVehicleRequirements: result.hourlyVehicleRequirements, // Pass hourly vehicle needs
        peakPeriods, // Auto-detected peak periods
        useDemandBasedAllocation: true, // Auto-enable demand-based allocation for trip analysis
      }));
    }
  }, []);

  // Generate fleet from config
  const generateFleet = useCallback(async (): Promise<GeneratedFleetData> => {
    try {
      logEvent({
        category: 'AutoFleetGenerator',
        action: 'FleetGenerationStarted',
        label: config.mode,
        vehicle_count: config.manualVehicleCount,
      });

      let vehicles: Vehicle[] = [];
      let shifts: GeneratedShift[] = [];
      let summary: GeneratedFleetData['summary'];
      let mixedFleetSummary: MixedFleetSummary | undefined;

      // Handle fixed routes mode
      if (config.mode === 'fixed-routes') {
        // Get selected routes from config
        const selectedRouteIds = config.selectedRouteIds || [];
        if (selectedRouteIds.length === 0) {
          throw new Error('No fixed routes selected. Please select at least one route.');
        }

        // Filter routes to only selected ones
        const selectedRoutes = allFixedRoutes.filter(route => 
          selectedRouteIds.includes(route.id)
        );

        if (selectedRoutes.length === 0) {
          throw new Error('Selected routes not found. Please refresh and try again.');
        }

        // Validate selected routes before processing
        const routeValidations = validateRoutes(selectedRoutes, globalTrafficMultiplier);
        const invalidRoutes = selectedRoutes.filter((route, index) => !routeValidations[index]?.isValid);
        
        if (invalidRoutes.length > 0) {
          const invalidNames = invalidRoutes.map(r => r.name).join(', ');
          const errorMessages = invalidRoutes
            .map((route, idx) => {
              const validation = routeValidations[selectedRoutes.indexOf(route)];
              return `${route.name}: ${validation?.errors.join('; ') || 'Invalid configuration'}`;
            })
            .join('\n');
          
          throw new Error(
            `Invalid route configuration detected:\n\n${errorMessages}\n\n` +
            `Please fix the route configurations in the Fixed Routes drawer before generating a fleet.`
          );
        }

        // Check for warnings (inform user but allow proceeding)
        const routesWithWarnings = selectedRoutes.filter((route, index) => 
          routeValidations[index]?.warnings && routeValidations[index].warnings.length > 0
        );
        if (routesWithWarnings.length > 0) {
          console.warn('Routes with warnings:', routesWithWarnings.map(r => r.name));
        }

        // Build fleet configuration
        const fleetConfig: FixedRouteFleetConfig = {
          routes: selectedRoutes,
          vehicleSharingMode: config.vehicleSharingMode || 'shared',
          vehicleSpecs: {
            standardSeats: config.standardSeats,
            wheelchairSeats: config.wheelchairSeats,
            startLocationId: config.startLocationId,
            endLocationId: config.endLocationId,
          },
          trafficMultiplier: globalTrafficMultiplier,
        };

        // Validate vehicle specifications
        if (config.standardSeats < 0 || config.wheelchairSeats < 0) {
          throw new Error('Vehicle seat counts must be non-negative.');
        }

        if (config.standardSeats === 0 && config.wheelchairSeats === 0) {
          throw new Error('Vehicle must have at least one seat (standard or wheelchair).');
        }

        // Calculate fleet using FleetOptimizer
        const optimizer = new FleetOptimizer();
        let fleetResult;
        try {
          fleetResult = optimizer.calculateFixedRouteFleet(selectedRoutes, fleetConfig);
        } catch (error) {
          throw new Error(
            `Fleet calculation failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\n` +
            `This may indicate a problem with route configurations or vehicle specifications.`
          );
        }

        // Validate fleet result
        if (!fleetResult || !fleetResult.vehicleAssignments || fleetResult.vehicleAssignments.length === 0) {
          throw new Error('Fleet calculation produced no vehicles. This may indicate an issue with route configurations.');
        }

        // Get initial vehicle assignments
        let vehicleAssignments = fleetResult.vehicleAssignments;

        // Apply shift allocation if enabled
        if (config.enableShiftAllocation) {
          try {
            vehicleAssignments = optimizer.allocateShiftsToFixedRouteFleet(vehicleAssignments, config);
          } catch (error) {
            throw new Error(
              `Shift allocation failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\n` +
              `This may indicate a problem with shift configuration or route assignments.`
            );
          }
        }

        // Convert vehicle assignments to Vehicle format
          try {
            vehicles = convertAssignmentsToFleet(vehicleAssignments, fleetConfig, availablePoints, mapLayers);
          } catch (error) {
            throw new Error(
              `Failed to convert fleet assignments: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
          }

        if (vehicles.length === 0) {
          throw new Error('No vehicles were generated. Please check your route configurations.');
        }

        // Convert to GeneratedShift format (one shift per vehicle with its operating periods)
        shifts = vehicles.map((vehicle) => ({
          vehicleId: vehicle.id,
          vehicleName: vehicle.name,
          operatingPeriods: vehicle.operatingHours,
        }));

        // Calculate summary statistics
        const totalBreaks = vehicles.reduce((sum, vehicle) => {
          return sum + vehicle.operatingHours.reduce((s, period) => s + (period.breaks?.length || 0), 0);
        }, 0);

        // Calculate total shifts (when shift allocation is enabled, vehicles.length = shifts, 
        // when disabled, each route assignment may be a separate period)
        const totalShifts = config.enableShiftAllocation 
          ? vehicles.length // Each vehicle represents one shift when shift allocation is enabled
          : shifts.reduce((sum, shift) => sum + shift.operatingPeriods.length, 0);

        // Calculate average shifts per vehicle (only meaningful when shift allocation is enabled)
        const uniqueVehicleIds = new Set(vehicles.map(v => {
          // Extract base vehicle ID (remove "- Shift N" suffix if present)
          return v.id.split(' - Shift ')[0];
        }));
        const averageShiftsPerVehicle = uniqueVehicleIds.size > 0 
          ? Math.round((totalShifts / uniqueVehicleIds.size) * 10) / 10
          : 1;

        let activeMinutes = 0;
        vehicles.forEach(vehicle => {
          vehicle.operatingHours.forEach(period => {
            const periodStart = parseTime(period.start);
            const periodEnd = parseTime(period.end);
            const periodDuration = calculateTimeDifference(periodStart, periodEnd);
            activeMinutes += periodDuration;
            
            if (period.breaks) {
              period.breaks.forEach((breakPeriod: BreakPeriod) => {
                const breakStart = parseTime(breakPeriod.start);
                const breakEnd = parseTime(breakPeriod.end);
                const breakDuration = calculateTimeDifference(breakStart, breakEnd);
                activeMinutes -= breakDuration;
              });
            }
          });
        });

        const activeVehicleHours = activeMinutes / 60;

        summary = {
          totalVehicles: vehicles.length,
          totalShifts,
          totalBreaks,
          servicePeriod: fleetResult.summary.operatingHoursCoverage,
          totalStandardSeats: vehicles.length * config.standardSeats,
          totalWheelchairSeats: vehicles.length * config.wheelchairSeats,
          totalCapacity: vehicles.length * (config.standardSeats + config.wheelchairSeats),
          activeVehicleHours: Math.round(activeVehicleHours * 10) / 10,
          totalVehicleHours: 0, // Not applicable for fixed routes in Phase 1A
          utilizationPercent: 0, // Not applicable for fixed routes in Phase 1A
        };
      } else if (config.mode === 'mixed') {
        // Mixed fleet mode: optimize both fixed routes and on-demand trips
        const selectedRouteIds = config.selectedRouteIds || [];
        const selectedLayerId = config.selectedLayerId;

        // Validate that at least one service type is selected
        if (selectedRouteIds.length === 0 && !selectedLayerId) {
          throw new Error('Please select at least one fixed route or one trip layer for mixed fleet generation.');
        }

        // Get selected routes
        const selectedRoutes = selectedRouteIds.length > 0
          ? allFixedRoutes.filter(route => selectedRouteIds.includes(route.id))
          : [];

        if (selectedRouteIds.length > 0 && selectedRoutes.length === 0) {
          throw new Error('Selected routes not found. Please refresh and try again.');
        }

        // Validate selected routes if any
        if (selectedRoutes.length > 0) {
          const routeValidations = validateRoutes(selectedRoutes, globalTrafficMultiplier);
          const invalidRoutes = selectedRoutes.filter((route, index) => !routeValidations[index]?.isValid);
          
          if (invalidRoutes.length > 0) {
            const errorMessages = invalidRoutes
              .map((route, idx) => {
                const validation = routeValidations[selectedRoutes.indexOf(route)];
                return `${route.name}: ${validation?.errors.join('; ') || 'Invalid configuration'}`;
              })
              .join('\n');
            
            throw new Error(
              `Invalid route configuration detected:\n\n${errorMessages}\n\n` +
              `Please fix the route configurations in the Fixed Routes drawer before generating a fleet.`
            );
          }
        }

        // Extract trips from selected layer if provided
        let trips: Line[] = [];
        if (selectedLayerId) {
          const selectedLayer = mapLayers.find(l => l.layerId.toString() === selectedLayerId);
          if (!selectedLayer) {
            throw new Error(`Selected trip layer not found. Please select a different layer or proceed with fixed routes only.`);
          }
          
          // Extract trip lines from layer
          trips = selectedLayer.shapes.filter(
            (shape): shape is Line => shape.type === 'line' && 'ride_id' in shape
          ) as Line[];

          if (trips.length === 0) {
            throw new Error('Selected trip layer contains no valid trips. Please select a different layer or proceed with fixed routes only.');
          }
        }

        // Validate that we have at least one service type with data
        if (selectedRoutes.length === 0 && trips.length === 0) {
          throw new Error('No routes or trips available. Please select at least one fixed route or one trip layer.');
        }

        // Validate vehicle specifications
        if (config.standardSeats < 0 || config.wheelchairSeats < 0) {
          throw new Error('Vehicle seat counts must be non-negative.');
        }

        if (config.standardSeats === 0 && config.wheelchairSeats === 0) {
          throw new Error('Vehicle must have at least one seat (standard or wheelchair).');
        }

        // Validate priority mode configuration
        if (!config.priorityMode) {
          throw new Error('Priority mode must be specified for mixed fleet generation.');
        }

        if (config.priorityMode === 'weighted') {
          if (config.fixedRouteWeight === undefined || config.onDemandWeight === undefined) {
            throw new Error('Weighted priority mode requires both fixed route weight and on-demand weight to be specified.');
          }
          if (config.fixedRouteWeight < 0 || config.fixedRouteWeight > 100) {
            throw new Error('Fixed route weight must be between 0 and 100.');
          }
          if (config.onDemandWeight < 0 || config.onDemandWeight > 100) {
            throw new Error('On-demand weight must be between 0 and 100.');
          }
        }

        // Handle case where only one service type is selected
        if (selectedRoutes.length === 0 && trips.length > 0) {
          // Only on-demand: fall back to on-demand fleet generation
          const generator = new ShiftGenerator(config);
          shifts = generator.generateShifts();
          
          // Helper function to get coordinates from a point ID (required)
          const getCoordinatesFromPointId = (pointId: string, locationType: 'start' | 'end'): { lat: number; lng: number } => {
            if (!pointId) {
              throw new Error(`Vehicle ${locationType} location ID is required but was not provided.`);
            }
            const point = availablePoints.find(p => p.id === pointId);
            if (!point) {
              throw new Error(
                `Vehicle ${locationType} location point with ID "${pointId}" not found. ` +
                `Please ensure the location point exists in the map layers.`
              );
            }
            if (!point.coordinates || point.coordinates.length < 2) {
              throw new Error(
                `Vehicle ${locationType} location point "${point.name || pointId}" has invalid coordinates. ` +
                `Expected [latitude, longitude] array.`
              );
            }
            const lat = point.coordinates[0];
            const lng = point.coordinates[1];
            
            // Validate coordinates are valid numbers
            if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
              throw new Error(
                `Vehicle ${locationType} location point "${point.name || pointId}" has invalid coordinate values. ` +
                `Latitude: ${lat}, Longitude: ${lng}`
              );
            }
            
            return {
              lat,
              lng
            };
          };
          
          vehicles = shifts.map((shift, index) => {
            const startCoords = getCoordinatesFromPointId(config.startLocationId, 'start');
            const endCoords = getCoordinatesFromPointId(config.endLocationId, 'end');
            const operatingHours = attachBreakLocations(shift.operatingPeriods, startCoords);

            return {
              id: shift.vehicleId,
              name: shift.vehicleName,
              standardSeats: config.standardSeats,
              wheelchairSeats: config.wheelchairSeats,
              operatingHours,
              startLocation: config.startLocationId,
              endLocation: config.endLocationId,
              startLocationCoordinates: startCoords,
              endLocationCoordinates: endCoords,
            };
          });
          
          // Calculate summary (same as on-demand mode above)
          const totalBreaks = shifts.reduce((sum, shift) => {
            return sum + shift.operatingPeriods.reduce((s, period) => s + (period.breaks?.length || 0), 0);
          }, 0);

          let activeMinutes = 0;
          shifts.forEach(shift => {
            shift.operatingPeriods.forEach(period => {
              const periodStart = parseTime(period.start);
              const periodEnd = parseTime(period.end);
              const periodDuration = calculateTimeDifference(periodStart, periodEnd);
              activeMinutes += periodDuration;
              
              if (period.breaks) {
                period.breaks.forEach((breakPeriod: BreakPeriod) => {
                  const breakStart = parseTime(breakPeriod.start);
                  const breakEnd = parseTime(breakPeriod.end);
                  const breakDuration = calculateTimeDifference(breakStart, breakEnd);
                  activeMinutes -= breakDuration;
                });
              }
            });
          });
          
          const serviceStart = parseTime(config.serviceStartTime);
          const serviceEnd = parseTime(config.serviceEndTime);
          const serviceDuration = calculateTimeDifference(serviceStart, serviceEnd);
          const totalMinutes = serviceDuration * vehicles.length;
          
          const activeVehicleHours = activeMinutes / 60;
          const totalVehicleHours = totalMinutes / 60;
          const utilizationPercent = totalVehicleHours > 0 ? (activeVehicleHours / totalVehicleHours) * 100 : 0;

          summary = {
            totalVehicles: vehicles.length,
            totalShifts: shifts.reduce((sum, shift) => sum + shift.operatingPeriods.length, 0),
            totalBreaks,
            servicePeriod: `${config.serviceStartTime} - ${config.serviceEndTime}`,
            totalStandardSeats: vehicles.length * config.standardSeats,
            totalWheelchairSeats: vehicles.length * config.wheelchairSeats,
            totalCapacity: vehicles.length * (config.standardSeats + config.wheelchairSeats),
            activeVehicleHours: Math.round(activeVehicleHours * 10) / 10,
            totalVehicleHours: Math.round(totalVehicleHours * 10) / 10,
            utilizationPercent: Math.round(utilizationPercent * 10) / 10,
          };
        } else if (trips.length === 0 && selectedRoutes.length > 0) {
          // Only fixed routes: fall back to fixed route fleet generation
          // (This case is handled by the fixed-routes branch above, but we can reuse logic)
          const fleetConfig: FixedRouteFleetConfig = {
            routes: selectedRoutes,
            vehicleSharingMode: config.vehicleSharingMode || 'shared',
            vehicleSpecs: {
              standardSeats: config.standardSeats,
              wheelchairSeats: config.wheelchairSeats,
              startLocationId: config.startLocationId,
              endLocationId: config.endLocationId,
            },
            trafficMultiplier: globalTrafficMultiplier,
          };

          const optimizer = new FleetOptimizer();
          let fleetResult;
          try {
            fleetResult = optimizer.calculateFixedRouteFleet(selectedRoutes, fleetConfig);
          } catch (error) {
            throw new Error(
              `Fleet calculation failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\n` +
              `This may indicate a problem with route configurations or vehicle specifications.`
            );
          }

          if (!fleetResult || !fleetResult.vehicleAssignments || fleetResult.vehicleAssignments.length === 0) {
            throw new Error('Fleet calculation produced no vehicles. This may indicate an issue with route configurations.');
          }

          let vehicleAssignments = fleetResult.vehicleAssignments;

          if (config.enableShiftAllocation) {
            try {
              vehicleAssignments = optimizer.allocateShiftsToFixedRouteFleet(vehicleAssignments, config);
            } catch (error) {
              throw new Error(
                `Shift allocation failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\n` +
                `This may indicate a problem with shift configuration or route assignments.`
              );
            }
          }

          try {
            vehicles = convertAssignmentsToFleet(vehicleAssignments, fleetConfig, availablePoints, mapLayers);
          } catch (error) {
            throw new Error(
              `Failed to convert fleet assignments: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
          }

          if (vehicles.length === 0) {
            throw new Error('No vehicles were generated. Please check your route configurations.');
          }

          shifts = vehicles.map((vehicle) => ({
            vehicleId: vehicle.id,
            vehicleName: vehicle.name,
            operatingPeriods: vehicle.operatingHours,
          }));

          const totalBreaks = vehicles.reduce((sum, vehicle) => {
            return sum + vehicle.operatingHours.reduce((s, period) => s + (period.breaks?.length || 0), 0);
          }, 0);

          const totalShifts = config.enableShiftAllocation 
            ? vehicles.length
            : shifts.reduce((sum, shift) => sum + shift.operatingPeriods.length, 0);

          let activeMinutes = 0;
          vehicles.forEach(vehicle => {
            vehicle.operatingHours.forEach(period => {
              const periodStart = parseTime(period.start);
              const periodEnd = parseTime(period.end);
              const periodDuration = calculateTimeDifference(periodStart, periodEnd);
              activeMinutes += periodDuration;
              
              if (period.breaks) {
                period.breaks.forEach((breakPeriod: BreakPeriod) => {
                  const breakStart = parseTime(breakPeriod.start);
                  const breakEnd = parseTime(breakPeriod.end);
                  const breakDuration = calculateTimeDifference(breakStart, breakEnd);
                  activeMinutes -= breakDuration;
                });
              }
            });
          });

          const activeVehicleHours = activeMinutes / 60;

          summary = {
            totalVehicles: vehicles.length,
            totalShifts,
            totalBreaks,
            servicePeriod: fleetResult.summary.operatingHoursCoverage,
            totalStandardSeats: vehicles.length * config.standardSeats,
            totalWheelchairSeats: vehicles.length * config.wheelchairSeats,
            totalCapacity: vehicles.length * (config.standardSeats + config.wheelchairSeats),
            activeVehicleHours: Math.round(activeVehicleHours * 10) / 10,
            totalVehicleHours: 0,
            utilizationPercent: 0,
          };
        } else {
          // Both service types: use mixed fleet optimization
          const mixedConfig: MixedFleetConfig = {
            routes: selectedRoutes,
            vehicleSharingMode: config.vehicleSharingMode || 'shared',
            vehicleSpecs: {
              standardSeats: config.standardSeats,
              wheelchairSeats: config.wheelchairSeats,
              startLocationId: config.startLocationId,
              endLocationId: config.endLocationId,
            },
            trafficMultiplier: globalTrafficMultiplier,
            priorityMode: config.priorityMode!,
            fixedRouteWeight: config.fixedRouteWeight,
            onDemandWeight: config.onDemandWeight,
            transitionTimeMinutes: config.transitionTimeMinutes || 0,
            useDemandBasedAllocation: config.useDemandBasedAllocation || false,
            hourlyVehicleRequirements: config.hourlyVehicleRequirements,
            serviceStartTime: config.serviceStartTime,
            serviceEndTime: config.serviceEndTime,
          };

          const optimizer = new FleetOptimizer();
          let fleetResult;
          try {
            fleetResult = optimizer.calculateMixedFleet(selectedRoutes, trips, mixedConfig);
          } catch (error) {
            throw new Error(
              `Mixed fleet calculation failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\n` +
              `This may indicate a problem with route configurations, trip data, or vehicle specifications.`
            );
          }

          if (!fleetResult || !fleetResult.vehicleAssignments || fleetResult.vehicleAssignments.length === 0) {
            throw new Error('Mixed fleet calculation produced no vehicles. This may indicate an issue with configurations.');
          }

          let vehicleAssignments = fleetResult.vehicleAssignments;

          // Apply shift allocation if enabled
          if (config.enableShiftAllocation) {
            try {
              vehicleAssignments = optimizer.allocateShiftsToFixedRouteFleet(vehicleAssignments, config);
            } catch (error) {
              throw new Error(
                `Shift allocation failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\n` +
                `This may indicate a problem with shift configuration or vehicle assignments.`
              );
            }
          }

          // Convert vehicle assignments to Vehicle format
          try {
            vehicles = convertAssignmentsToFleet(vehicleAssignments, mixedConfig, availablePoints, mapLayers);
          } catch (error) {
            throw new Error(
              `Failed to convert fleet assignments: ${error instanceof Error ? error.message : 'Unknown error'}`
            );
          }

          if (vehicles.length === 0) {
            throw new Error('No vehicles were generated. Please check your configurations.');
          }

          // Convert to GeneratedShift format
          shifts = vehicles.map((vehicle) => ({
            vehicleId: vehicle.id,
            vehicleName: vehicle.name,
            operatingPeriods: vehicle.operatingHours,
          }));

          // Calculate summary statistics
          const totalBreaks = vehicles.reduce((sum, vehicle) => {
            return sum + vehicle.operatingHours.reduce((s, period) => s + (period.breaks?.length || 0), 0);
          }, 0);

          const totalShifts = config.enableShiftAllocation 
            ? vehicles.length
            : shifts.reduce((sum, shift) => sum + shift.operatingPeriods.length, 0);

          let activeMinutes = 0;
          vehicles.forEach(vehicle => {
            vehicle.operatingHours.forEach(period => {
              const periodStart = parseTime(period.start);
              const periodEnd = parseTime(period.end);
              const periodDuration = calculateTimeDifference(periodStart, periodEnd);
              activeMinutes += periodDuration;
              
              if (period.breaks) {
                period.breaks.forEach((breakPeriod: BreakPeriod) => {
                  const breakStart = parseTime(breakPeriod.start);
                  const breakEnd = parseTime(breakPeriod.end);
                  const breakDuration = calculateTimeDifference(breakStart, breakEnd);
                  activeMinutes -= breakDuration;
                });
              }
            });
          });

          const activeVehicleHours = activeMinutes / 60;

          // Use mixed fleet summary if available
          mixedFleetSummary = fleetResult.mixedFleetSummary;
          const servicePeriod = mixedFleetSummary 
            ? `${fleetResult.summary.operatingHoursCoverage}`
            : fleetResult.summary.operatingHoursCoverage;

          summary = {
            totalVehicles: vehicles.length,
            totalShifts,
            totalBreaks,
            servicePeriod,
            totalStandardSeats: vehicles.length * config.standardSeats,
            totalWheelchairSeats: vehicles.length * config.wheelchairSeats,
            totalCapacity: vehicles.length * (config.standardSeats + config.wheelchairSeats),
            activeVehicleHours: Math.round(activeVehicleHours * 10) / 10,
            totalVehicleHours: mixedFleetSummary ? Math.round(mixedFleetSummary.totalVehicleHours * 10) / 10 : 0,
            utilizationPercent: mixedFleetSummary ? Math.round(mixedFleetSummary.utilizationPercent * 10) / 10 : 0,
          };
        }
      } else {
        if (config.mode === 'trip-layer') {
          const selection = getTripLayersFromContext(mapLayers).find(
            (layer) => String(layer.layerId) === String(config.selectedLayerId || '')
          );
          if (!selection) {
            throw new Error('No trip layer found. Please select a valid trip layer.');
          }
          const centroidPoint = {
            pointId: config.startLocationId,
            coordinates: (() => {
              const point = availablePoints.find((p) => p.id === config.startLocationId);
              if (!point || !point.coordinates || point.coordinates.length < 2) {
                throw new Error('Start location point not found. Please select a valid start location.');
              }
              return { lat: point.coordinates[0], lng: point.coordinates[1] };
            })(),
          };

          const generated = generateFleetFromTripLayerAnalysis({
            layer: selection,
            centroidPoint,
            capacityStandard: config.standardSeats,
            capacityWheelchair: config.wheelchairSeats,
            selectedLayerId: config.selectedLayerId,
            servicePreset: config.tripsPerVehiclePerHour <= 3 ? 'paratransit' : 'on-demand',
          });

          vehicles = generated.vehicles;
          shifts = generated.shifts;
          summary = generated.summary;
        } else {
          // Use shift generator for manual on-demand services
          const generator = new ShiftGenerator(config);
          shifts = generator.generateShifts();

          if (shifts.length === 0 && config.useDemandBasedAllocation) {
            const fallbackGenerator = new ShiftGenerator({
              ...config,
              useDemandBasedAllocation: false,
            });
            shifts = fallbackGenerator.generateShifts();
          }

          if (shifts.length === 0) {
            throw new Error(
              'No shifts generated. Try extending your service window or disabling demand-based allocation after setting a manual vehicle count.'
            );
          }

          // Convert to Vehicle format
          // Helper function to get coordinates from a point ID (required)
          // Also checks map layers directly in case point was just created
          const getCoordinatesFromPointId = (pointId: string, locationType: 'start' | 'end'): { lat: number; lng: number } => {
            if (!pointId) {
              throw new Error(`Vehicle ${locationType} location ID is required but was not provided.`);
            }
          
          // First check availablePoints
          let point = availablePoints.find(p => p.id === pointId);
          
          // If not found, check map layers directly (for newly created inferred points)
          if (!point) {
            const allPointsFromLayers = mapLayers.flatMap(layer =>
              layer.shapes.filter((s): s is Point => s.type === 'point')
            );
            point = allPointsFromLayers.find(p => p.id === pointId);
          }
          
          if (!point) {
            throw new Error(
              `Vehicle ${locationType} location point with ID "${pointId}" not found. ` +
              `Please ensure the location point exists in the map layers.`
            );
          }
          if (!point.coordinates || point.coordinates.length < 2) {
            throw new Error(
              `Vehicle ${locationType} location point "${point.name || pointId}" has invalid coordinates. ` +
              `Expected [latitude, longitude] array.`
            );
          }
          const lat = point.coordinates[0];
          const lng = point.coordinates[1];
          
          // Validate coordinates are valid numbers
          if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
            throw new Error(
              `Vehicle ${locationType} location point "${point.name || pointId}" has invalid coordinate values. ` +
              `Latitude: ${lat}, Longitude: ${lng}`
            );
          }
          
          return {
            lat,
            lng
          };
        };

          vehicles = shifts.map((shift) => {
            const startCoords = getCoordinatesFromPointId(config.startLocationId, 'start');
            const endCoords = getCoordinatesFromPointId(config.endLocationId, 'end');
            const operatingHours = attachBreakLocations(shift.operatingPeriods, startCoords);

            return {
              id: shift.vehicleId,
              name: shift.vehicleName,
              standardSeats: config.standardSeats,
              wheelchairSeats: config.wheelchairSeats,
              operatingHours,
              startLocation: config.startLocationId,
              endLocation: config.endLocationId,
              startLocationCoordinates: startCoords,
              endLocationCoordinates: endCoords,
            };
          });

        // Calculate summary statistics
        const totalBreaks = shifts.reduce((sum, shift) => {
          return sum + shift.operatingPeriods.reduce((s, period) => s + (period.breaks?.length || 0), 0);
        }, 0);

        // Calculate active vehicle hours (working time excluding breaks)
        let activeMinutes = 0;
        let totalBreakMinutes = 0;
        
        shifts.forEach(shift => {
          shift.operatingPeriods.forEach(period => {
            const periodStart = parseTime(period.start);
            const periodEnd = parseTime(period.end);
            const periodDuration = calculateTimeDifference(periodStart, periodEnd);
            
            // Add period duration
            activeMinutes += periodDuration;
            
            // Subtract break time
            if (period.breaks) {
              period.breaks.forEach((breakPeriod: BreakPeriod) => {
                const breakStart = parseTime(breakPeriod.start);
                const breakEnd = parseTime(breakPeriod.end);
                const breakDuration = calculateTimeDifference(breakStart, breakEnd);
                totalBreakMinutes += breakDuration;
                activeMinutes -= breakDuration;
              });
            }
          });
        });
        
        // Calculate total available vehicle hours
        const serviceStart = parseTime(config.serviceStartTime);
        const serviceEnd = parseTime(config.serviceEndTime);
        const serviceDuration = calculateTimeDifference(serviceStart, serviceEnd);
        const totalMinutes = serviceDuration * vehicles.length;
        
        const activeVehicleHours = activeMinutes / 60;
        const totalVehicleHours = totalMinutes / 60;
        const utilizationPercent = totalVehicleHours > 0 ? (activeVehicleHours / totalVehicleHours) * 100 : 0;

          summary = {
            totalVehicles: vehicles.length,
            totalShifts: shifts.reduce((sum, shift) => sum + shift.operatingPeriods.length, 0),
            totalBreaks,
            servicePeriod: `${config.serviceStartTime} - ${config.serviceEndTime}`,
            totalStandardSeats: vehicles.length * config.standardSeats,
            totalWheelchairSeats: vehicles.length * config.wheelchairSeats,
            totalCapacity: vehicles.length * (config.standardSeats + config.wheelchairSeats),
            activeVehicleHours: Math.round(activeVehicleHours * 10) / 10, // Round to 1 decimal
            totalVehicleHours: Math.round(totalVehicleHours * 10) / 10,
            utilizationPercent: Math.round(utilizationPercent * 10) / 10,
          };
        }
      }

      // Generate default fleet name if not set
      if (!fleetName) {
        const timestamp = new Date().toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        let layerName = 'Manual';
        if (config.mode === 'trip-layer' && config.selectedLayerId) {
          layerName = mapLayers.find((l) => l.layerId.toString() === config.selectedLayerId)?.layerName || 'Trip Layer';
        } else if (config.mode === 'mixed') {
          const routeCount = config.selectedRouteIds?.length || 0;
          const hasLayer = !!config.selectedLayerId;
          if (routeCount > 0 && hasLayer) {
            layerName = `Mixed (${routeCount} route${routeCount !== 1 ? 's' : ''} + On-Demand)`;
          } else if (routeCount > 0) {
            layerName = `Mixed (${routeCount} route${routeCount !== 1 ? 's' : ''} only)`;
          } else if (hasLayer) {
            layerName = 'Mixed (On-Demand only)';
          }
        } else if (config.mode === 'fixed-routes') {
          const routeCount = config.selectedRouteIds?.length || 0;
          layerName = `${routeCount} Route${routeCount !== 1 ? 's' : ''}`;
        }
        setFleetName(`Auto Fleet - ${layerName} - ${timestamp}`);
      }

      const fleetData: GeneratedFleetData = {
        fleetName: fleetName || `Auto Fleet ${new Date().toLocaleTimeString()}`,
        vehicles,
        shifts,
        summary,
        mixedFleetSummary,
      };

      logEvent({
        category: 'AutoFleetGenerator',
        action: 'FleetGenerationCompleted',
        label: config.mode,
        value: vehicles.length,
        total_vehicles: vehicles.length,
        total_shifts: summary.totalShifts,
      });

      return fleetData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during fleet generation';
      
      logEvent({
        category: 'AutoFleetGenerator',
        action: 'FleetGenerationFailed',
        label: config.mode,
        error: errorMessage,
      });

      throw error;
    }
  }, [config, fleetName, mapLayers, allFixedRoutes, globalTrafficMultiplier]);

  const editableFleetData = useMemo(() => {
    if (!generatedFleetData) return null;
    if (previewSource !== 'edited') return generatedFleetData;

    const [serviceStartTimeRaw, serviceEndTimeRaw] = generatedFleetData.summary.servicePeriod.split(' - ');
    const serviceStartTime = serviceStartTimeRaw || config.serviceStartTime;
    const serviceEndTime = serviceEndTimeRaw || config.serviceEndTime;
    const serviceStartMinutes = parseTime(serviceStartTime);
    const serviceEndMinutes = parseTime(serviceEndTime);
    const intervalMinutes = 30;
    const totalBlocks = Math.max(0, Math.ceil((serviceEndMinutes - serviceStartMinutes) / intervalMinutes));

    const sourceShifts =
      config.mode === 'fixed-routes' || config.mode === 'mixed'
        ? convertVehiclesToGeneratedShifts(generatedFleetData.vehicles)
        : generatedFleetData.shifts;

    const editedShifts: GeneratedShift[] = sourceShifts.map((shift) => {
      const statuses = Array.from({ length: totalBlocks }, (_, blockIndex) => {
        const blockStartMinutes = serviceStartMinutes + blockIndex * intervalMinutes;
        const slotKey = toTimelineSlotKey(shift.vehicleId, blockStartMinutes);
        return (
          timelineSlotOverrides[slotKey] ??
          getOperatingPeriodStatusAtBlock(shift.operatingPeriods, blockStartMinutes, intervalMinutes)
        );
      });

      return {
        ...shift,
        operatingPeriods: buildOperatingPeriodsFromStatuses(statuses, serviceStartMinutes, intervalMinutes, config.maxShiftTimeMinutes),
      };
    });

    const editedVehicles: Vehicle[] = generatedFleetData.vehicles.map((vehicle) => {
      const editedShift = editedShifts.find((shift) => shift.vehicleId === vehicle.id);
      if (!editedShift) return vehicle;
      return {
        ...vehicle,
        operatingHours: editedShift.operatingPeriods,
      };
    });

    const summary = recalculateFleetSummary(editedVehicles, serviceStartTime, serviceEndTime);

    return {
      ...generatedFleetData,
      vehicles: editedVehicles,
      shifts: editedShifts,
      editedShifts,
      summary,
    };
  }, [generatedFleetData, previewSource, config.serviceStartTime, config.serviceEndTime, config.mode, config.maxShiftTimeMinutes, timelineSlotOverrides]);

  const handleTimelineBlockToggle = useCallback(
    (vehicleId: string, blockStartMinutes: number, status: 'working' | 'break' | 'off') => {
      const slotKey = toTimelineSlotKey(vehicleId, blockStartMinutes);
      const nextStatus = getNextTimelineStatus(status);
      setTimelineSlotOverrides((prev) => ({
        ...prev,
        [slotKey]: nextStatus,
      }));
      setPreviewSource('edited');
    },
    []
  );

  const handleResetTimelineEdits = useCallback(() => {
    setTimelineSlotOverrides({});
    setPreviewSource('generated');
  }, []);

  // Handle next button
  const handleNext = async () => {
    if (activeStep === STEP_KEYS.length - 2) {
      // Moving from Configuration to Preview - generate fleet
      setIsGenerating(true);
      setGenerationError(null);

      try {
        const fleetData = await generateFleet();
        setGeneratedFleetData(fleetData);
        setTimelineSlotOverrides({});
        setPreviewSource('generated');
        setActiveStep(prev => prev + 1);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to generate fleet';
        setGenerationError(errorMessage);
      } finally {
        setIsGenerating(false);
      }
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  // Handle close with debounce to prevent double-triggering
  const handleCloseRef = useRef(false);
  const handleClose = useCallback(() => {
    if (handleCloseRef.current) return; // Prevent double-triggering
    handleCloseRef.current = true;
    
    logEvent({
      category: 'AutoFleetGenerator',
      action: 'ModalClosed',
      label: `Step ${activeStep + 1}`,
      step: activeStep,
    });

    // Reset state
    setActiveStep(0);
    setConfig(getDefaultConfig());
    setGeneratedFleetData(null);
    setTimelineSlotOverrides({});
    setPreviewSource('generated');
    setFleetName('');
    setGenerationError(null);
    setTripAnalysisResult(undefined);

    // Reset flag after a short delay
    setTimeout(() => {
      handleCloseRef.current = false;
    }, 100);

    onClose();
  }, [activeStep, onClose]);

  // Handle back button
  const handleBack = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    } else {
      handleClose();
    }
  }, [activeStep, handleClose]);

  // Handle regenerate
  const handleRegenerate = async () => {
    setIsGenerating(true);
    setGenerationError(null);

    try {
      const fleetData = await generateFleet();
      setGeneratedFleetData(fleetData);
      setTimelineSlotOverrides({});
      setPreviewSource('generated');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to regenerate fleet';
      setGenerationError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle create fleet
  const handleCreateFleet = () => {
    const authoritativeFleetData = editableFleetData ?? generatedFleetData;
    if (!authoritativeFleetData) return;

    logEvent({
      category: 'AutoFleetGenerator',
      action: 'FleetCreated',
      label: fleetName,
      value: authoritativeFleetData.vehicles.length,
    });

    onFleetCreated(fleetName || authoritativeFleetData.fleetName, authoritativeFleetData.vehicles);
    handleClose();
  };

  // Check if we can proceed to next step
  const canProceed = () => {
    switch (activeStep) {
      case 0: // Mode selection
        if (config.mode === 'fixed-routes') {
          // For fixed routes: require at least one route selected
          const selectedRoutes = allFixedRoutes.filter(
            (route) => config.selectedRouteIds?.includes(route.id)
          );
          return selectedRoutes.length > 0;
        }
        if (config.mode === 'mixed') {
          const hasSelectedRoutes = (config.selectedRouteIds?.length || 0) > 0;
          const hasSelectedLayer = !!config.selectedLayerId;
          return hasSelectedRoutes || hasSelectedLayer;
        }
        if (config.mode === 'trip-layer') {
          return !!config.selectedLayerId && !!tripAnalysisResult;
        }
        return (config.manualVehicleCount ?? 0) > 0 && !!config.serviceStartTime && !!config.serviceEndTime;
      
      case 1: // Configuration
        if (config.mode === 'fixed-routes') {
          // For fixed routes: validate vehicle specs and that routes still exist
          const selectedRoutes = allFixedRoutes.filter(
            (route) => config.selectedRouteIds?.includes(route.id)
          );
          return (
            selectedRoutes.length > 0 &&
            config.standardSeats >= 0 &&
            config.wheelchairSeats >= 0
          );
        }
        if (config.mode === 'mixed') {
          return (
            config.standardSeats >= 0 &&
            config.wheelchairSeats >= 0 &&
            !!config.priorityMode
          );
        }
        return config.standardSeats >= 0 && config.wheelchairSeats >= 0;
      
      case 2: // Preview
        return !!generatedFleetData && !!fleetName;
      
      default:
        return false;
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        // Always show ModeSelectionStep - it handles all modes internally
        return (
          <ModeSelectionStep
            config={config}
            onConfigChange={handleConfigChange}
            onAnalysisComplete={handleAnalysisComplete}
            layers={mapLayers}
          />
        );
      
      case 1:
        // Show FixedRouteConfigurationStep when mode is 'fixed-routes'
        if (config.mode === 'fixed-routes') {
          return (
            <FixedRouteConfigurationStep
              config={config}
              onConfigChange={handleConfigChange}
              availablePoints={availablePoints}
            />
          );
        }
        // Show MixedServiceConfigurationStep when mode is 'mixed'
        if (config.mode === 'mixed') {
          return (
            <MixedServiceConfigurationStep
              config={config}
              onConfigChange={handleConfigChange}
              availablePoints={availablePoints}
              layers={mapLayers}
            />
          );
        }
        // Otherwise show regular ConfigurationStep
        return (
          <ConfigurationStep
            config={config}
            onConfigChange={handleConfigChange}
            availablePoints={availablePoints}
            analysisResult={tripAnalysisResult}
          />
        );
      
      case 2:
        return (
          <PreviewStep
            generatedFleetData={editableFleetData}
            fleetName={fleetName}
            onFleetNameChange={setFleetName}
            onRegenerate={handleRegenerate}
            peakPeriods={config.peakPeriods}
            mode={config.mode}
            interactive
            editedSource={previewSource}
            onBlockClick={handleTimelineBlockToggle}
            onResetEdits={handleResetTimelineEdits}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth={false}
      fullWidth
      sx={{
        zIndex: 2500,
        '& .MuiDialog-paper': {
          width: '85%',
          maxWidth: '85%',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Icon size={20} d={ICON_FALLBACKS.AutoFixHigh} />
            {t('autoFleetGen.modal.title')}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Icon size={20} name="xmark" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent 
        dividers 
        sx={{ 
          maxHeight: 'calc(90vh - 200px)', 
          overflowY: 'auto',
          p: 3,
        }}
      >
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEP_KEYS.map((stepKey) => (
            <Step key={stepKey}>
              <StepLabel>{t(`autoFleetGen.modal.steps.${stepKey}`)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Display */}
        {generationError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setGenerationError(null)}>
            {generationError}
          </Alert>
        )}

        {/* Step Content */}
        <Box sx={{ minHeight: 400 }}>
          {renderStepContent()}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          {t('autoFleetGen.modal.cancel')}
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleBack();
            }}
            disabled={isGenerating}
          >
            {t('autoFleetGen.modal.back')}
          </Button>
        )}
        {activeStep < STEP_KEYS.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed() || isGenerating}
          >
            {activeStep === STEP_KEYS.length - 2
              ? isGenerating
                ? t('autoFleetGen.modal.generating')
                : t('autoFleetGen.modal.generateFleet')
              : t('autoFleetGen.modal.next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={handleCreateFleet}
            disabled={!canProceed()}
          >
            {t('autoFleetGen.modal.createFleet')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AutoFleetGeneratorModal;

